import type { SelectOption, Meridiem } from '@theme/theme.d';
import { DateTime } from 'luxon';

const alphaNumericOptions =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

export const generateRandomString = (
  length: number,
  options: string = alphaNumericOptions
): string => {
  let result = '';
  const charactersLength = options.length;

  for (let i = 0; i < length; i++) {
    result += options.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
};

export const generateRandomNumber = (digits: number = 1) => {
  const firstDigit = Math.floor(Math.random() * 8) + 2; // Range from 2 to 9
  const remainingDigits = Array.from({ length: digits - 1 }, () =>
    Math.floor(Math.random() * 10)
  ).join('');
  return parseInt(firstDigit + remainingDigits);
};

export const generateUniqueKey = (keyName: string): string => {
  if (!keyName) {
    throw new Error('Key name is required');
  }
  return `${keyName}__${generateRandomString(6)}`;
};

export const isSvg = (src: string): boolean => {
  return src.endsWith('.svg');
};

export const padNumber = (
  seed: number,
  length: number = 1,
  padString: string = '0'
) => {
  return String(seed).padStart(length, padString);
};

export const formatTimeInputValue = (
  hour: number,
  minute: number,
  meridiem: Meridiem
): SelectOption => {
  return {
    label: `${formatHours(hour)}:${padNumber(minute, 2)} ${meridiem}`,
    value: `${padNumber(hour, 2)}:${padNumber(minute, 2)}`,
  };
};

export const normalizeTimeToStep = (
  hour: number,
  minute: number,
  step: number
): DateTime => {
  const dt = DateTime.fromObject({ hour, minute });
  const roundedMinutes = Math.ceil(dt.minute / step) * step;

  return roundedMinutes >= 60
    ? dt.set({ minute: 0 }).plus({ hours: 1 })
    : dt.set({ minute: roundedMinutes });
};

export const stripUnit = (value: string): number => {
  return parseInt(value.replace(/\D/g, ''));
};

export const getTime = () => {
  const now = DateTime.now();
  const hour = now.hour < 12 ? now.hour : now.hour + 1 - 12;
  const meridiem = now.hour < 12 ? 'AM' : 'PM';
  const time: SelectOption = {
    label: `${hour}:00 ${meridiem}`,
    value: `${now.hour + 1}:00`,
  };
  return time;
};

export const getAdjustedTime = (
  hours: string = '',
  minutes: string = ''
): SelectOption => {
  let time: SelectOption;
  let meridiem = 'AM';

  if (hours === '' && minutes === '') {
    const now = DateTime.now();
    const plusFive = now.plus({ minutes: 5 });
    meridiem = plusFive.hour < 12 ? 'AM' : 'PM';
    time = {
      label: `${plusFive.hour}:${padNumber(plusFive.minute, 2)} ${meridiem}`,
      value: `${plusFive.hour}:${padNumber(plusFive.minute, 2)}`,
    };
  } else {
    const parsedHours = typeof hours === 'string' ? parseInt(hours) : hours;
    const parsedMinutes =
      typeof minutes === 'string' ? parseInt(minutes) : minutes;

    meridiem = parsedHours < 12 ? 'AM' : 'PM';
    time = {
      label: `${parsedHours}:${padNumber(parsedMinutes, 2)} ${meridiem}`,
      value: `${parsedHours}:${padNumber(parsedMinutes, 2)}`,
    };
  }
  return time;
};

export const formatHours = (hours: number): number => {
  if (hours > 12) {
    return hours - 12;
  }
  if (hours === 0) {
    return 12;
  }
  return hours;
};

const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

export const getStringDate = (): string => {
  return formatDate(new Date());
};

export const getFutureStringDate = (daysToAdd: number): string => {
  const today = new Date();
  today.setDate(today.getDate() + daysToAdd);
  return formatDate(today);
};

export const getComponentClassName = (
  bemName: string,
  utilityClasses?: string[]
): string => {
  const arr: string[] = [bemName];
  if (utilityClasses) {
    arr.push(...utilityClasses);
  }

  return arr.join(' ');
};

export const roundToNearestMinuteStep = (
  steps: number,
  currentMinute: number
): number => {
  return Math.ceil(currentMinute / steps) * steps;
};
